/**
 * Native-app deep-link launcher.
 *
 * Tries to open the corresponding native app installed on the user's
 * device. If the app isn't installed, falls back to the relevant store.
 *
 * MECHANICS
 * ─────────
 *
 * iOS — Universal Links
 *   Tapping `https://askeili.com<path>` while iOS knows the AASA
 *   (`/.well-known/apple-app-site-association`) auto-routes into the
 *   installed app. If the app isn't installed, Safari just navigates
 *   the URL normally. We can't directly query install state, so we
 *   use a visibility-change probe: navigate, wait 1500ms, and if the
 *   page never went hidden assume the app is missing → redirect to the
 *   App Store as a fallback.
 *
 * Android — Intent URL
 *   `intent://askeili.com<path>#Intent;scheme=https;package=com.askeili.android;S.browser_fallback_url=<encoded play url>;end`
 *   Chrome reads this and: launches the named package if installed,
 *   otherwise navigates to the fallback URL. The browser handles the
 *   whole fallback chain — no JS probes required.
 *
 * Note on Android App Links auto-verification: the explicit `package=`
 * parameter in the intent URL means we DON'T depend on the
 * `assetlinks.json` SHA fingerprint matching. The intent goes directly
 * to that package. If assetlinks.json is wrong, plain-https taps from
 * other apps won't auto-route, but our button always will.
 */

const APP_STORE_URL = 'https://apps.apple.com/us/app/eili-bible-study-tool/id6761586127';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.askeili.android';
const ANDROID_PACKAGE = 'com.askeili.android';

/** Detect iOS/iPadOS — covers the iPad-as-Mac Safari signature. */
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Detect Android. */
function isAndroid() {
  return /android/i.test(navigator.userAgent);
}

/**
 * Try to open the native app at the given in-app path.
 * Falls back to the appropriate store after a short delay if the
 * deep-link tap didn't successfully launch the app.
 *
 * @param {string} path — the path inside the app to deep-link to,
 *   e.g. "/community/requests" or "/chat?conversation=xyz". Defaults
 *   to the current page's path so "Open in App" lands them where they
 *   already were.
 * @returns {Promise<void>} resolves when either the app launched or
 *   the fallback redirect fired.
 */
export function openInNativeApp(path) {
  const target = path || (window.location.pathname + window.location.search);

  if (isIOS()) {
    return openIOS(target);
  }
  if (isAndroid()) {
    return openAndroid(target);
  }
  // Desktop / other — there's no useful "open in app" path; just open
  // the App Store on Mac (which routes to the iPad/Mac App Store) for
  // discovery, and Play Store on everything else.
  window.open(navigator.platform === 'MacIntel' ? APP_STORE_URL : PLAY_STORE_URL, '_blank');
  return Promise.resolve();
}

/** iOS Universal Link with a visibility-change fallback to App Store. */
function openIOS(path) {
  return new Promise((resolve) => {
    const universal = `https://askeili.com${path.startsWith('/') ? '' : '/'}${path}`;
    let opened = false;

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // App took over — cancel the fallback.
        opened = true;
        cleanup();
        resolve();
      }
    };
    const onPageHide = () => {
      opened = true;
      cleanup();
      resolve();
    };
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    // Trigger the universal link. On iOS, navigating to a domain whose
    // AASA matches the installed app routes into the app instead of
    // navigating Safari. We use location.href rather than window.open
    // because window.open on iOS is rate-limited outside a click stack
    // and frequently blocked.
    try {
      window.location.href = universal;
    } catch {
      // Fall through to fallback
    }

    // 1.6s window — long enough for iOS to route into the app on a
    // typical device, short enough that users don't wait awkwardly.
    setTimeout(() => {
      if (opened) return;
      cleanup();
      // App didn't intercept — go to App Store.
      window.location.href = APP_STORE_URL;
      resolve();
    }, 1600);
  });
}

/** Android Intent URL with built-in browser fallback to Play Store. */
function openAndroid(path) {
  return new Promise((resolve) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    // Strip the leading slash for the intent host+path portion.
    const hostPath = `askeili.com${cleanPath}`;
    const fallback = encodeURIComponent(PLAY_STORE_URL);
    // Per Chrome docs:
    //   intent://<host><path>#Intent;scheme=<scheme>;package=<pkg>;
    //     S.browser_fallback_url=<encoded url>;end
    const intentUrl =
      `intent://${hostPath}` +
      `#Intent;scheme=https;package=${ANDROID_PACKAGE};` +
      `S.browser_fallback_url=${fallback};end`;

    try {
      window.location.href = intentUrl;
    } catch {
      // If the intent URL fails to construct (very old Android), just
      // open the Play Store directly.
      window.location.href = PLAY_STORE_URL;
    }
    // Chrome handles the fallback itself — we don't need a JS timer.
    resolve();
  });
}
